import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Disc3, Pause, Play, Shuffle } from 'lucide-react';
import { API_URL } from '../../utils/api';
import { bpmFromStyle } from '../../utils/reflectFlow';
import { GROUP_HEX } from '../../utils/groupColors';
import { normalizePack } from '../../utils/packModel';
import {
  isCompatibleRatio,
  pickPair,
  pickRandomPack,
  scheduleBuffers,
  sortPacksByTempo,
} from '../../utils/beatClock';

const audioContextType = () => window.AudioContext || window.webkitAudioContext;

const prioritizedLoops = (grouped, preferred = {}) => {
  const drums = grouped.Drums || [];
  const melodies = grouped.Melodies || [];
  const preferredDrum = drums.find((loop) => loop.loop_id === preferred.drums_id);
  const preferredMelody = melodies.find((loop) => loop.loop_id === preferred.melody_id);
  const ordered = [];
  const add = (loop, group) => {
    if (loop && !ordered.some((item) => item.loop.loop_id === loop.loop_id)) {
      ordered.push({ loop, group });
    }
  };
  add(preferredDrum, 'Drums');
  add(preferredMelody, 'Melodies');
  const length = Math.max(drums.length, melodies.length);
  for (let index = 0; index < length; index += 1) {
    add(drums[index], 'Drums');
    add(melodies[index], 'Melodies');
  }
  return ordered.slice(0, 6);
};

const BeatStrip = ({ color, onChangeBeat, onChangeBpm, onFirstPlay, onPlayingChange, song }) => {
  const [packs, setPacks] = useState([]);
  const [pack, setPack] = useState(null);
  const [grouped, setGrouped] = useState(null);
  const [decoded, setDecoded] = useState([]);
  const [drum, setDrum] = useState(null);
  const [melody, setMelody] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [singleLoop, setSingleLoop] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.72);
  const contextRef = useRef(null);
  const gainRef = useRef(null);
  const playbackRef = useRef(null);
  const fallbackRef = useRef(null);
  const firstPlayRef = useRef(false);
  const reopeningRef = useRef(null);
  const bpm = bpmFromStyle(song.style) || 90;
  const sortedPacks = useMemo(() => sortPacksByTempo(packs, bpm), [bpm, packs]);

  useEffect(() => {
    onPlayingChange?.(playing);
  }, [onPlayingChange, playing]);

  const getContext = useCallback(() => {
    if (!contextRef.current) {
      const AudioContext = audioContextType();
      if (!AudioContext) return null;
      const context = new AudioContext();
      const gain = context.createGain();
      gain.gain.value = volume;
      gain.connect(context.destination);
      contextRef.current = context;
      gainRef.current = gain;
    }
    if (contextRef.current.state === 'suspended') contextRef.current.resume();
    return contextRef.current;
  }, [volume]);

  const stopPlayback = useCallback(() => {
    playbackRef.current?.stop();
    playbackRef.current = null;
    if (fallbackRef.current) {
      fallbackRef.current.pause();
      fallbackRef.current.currentTime = 0;
      fallbackRef.current = null;
    }
    setPlaying(false);
  }, []);

  useEffect(() => () => {
    playbackRef.current?.stop();
    fallbackRef.current?.pause();
    contextRef.current?.close();
  }, []);

  useEffect(() => {
    if (gainRef.current) gainRef.current.gain.value = volume;
    if (fallbackRef.current) fallbackRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    let live = true;
    fetch(`${API_URL}/api/sound-packs`)
      .then((response) => {
        if (!response.ok) throw new Error('Could not load beat packs.');
        return response.json();
      })
      .then((data) => {
        if (live) setPacks((Array.isArray(data) ? data : []).map(normalizePack));
      })
      .catch((requestError) => {
        if (live) setError(requestError.message);
      });
    return () => { live = false; };
  }, []);

  const decodeLoops = useCallback(async (loops, preferred, context) => {
    const results = [];
    for (const item of prioritizedLoops(loops, preferred)) {
      if (!item.loop.preview_url) continue;
      try {
        const response = await fetch(item.loop.preview_url);
        if (!response.ok) throw new Error('Audio unavailable');
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        results.push({ ...item.loop, group: item.group, buffer, duration: buffer.duration });
      } catch (_error) {
        // Keep trying in pack order up to the six candidate cap.
      }
    }
    return results;
  }, []);

  const chooseDecoded = useCallback((items, selectedPack, preferred = {}) => {
    const drums = items.filter((loop) => loop.group === 'Drums');
    const melodies = items.filter((loop) => loop.group === 'Melodies');
    const orderedDrums = [
      ...drums.filter((loop) => loop.loop_id === preferred.drums_id),
      ...drums.filter((loop) => loop.loop_id !== preferred.drums_id),
    ];
    const orderedMelodies = [
      ...melodies.filter((loop) => loop.loop_id === preferred.melody_id),
      ...melodies.filter((loop) => loop.loop_id !== preferred.melody_id),
    ];
    const pair = pickPair(orderedDrums, orderedMelodies);
    const nextDrum = pair?.drums || (!orderedMelodies.length ? orderedDrums[0] : null);
    const nextMelody = pair?.melody || orderedMelodies[0] || null;
    setDecoded(items);
    setDrum(nextDrum);
    setMelody(nextMelody);
    setSingleLoop(!pair && Boolean(nextDrum || nextMelody));
    if (nextDrum || nextMelody) {
      onChangeBeat({
        pack_id: selectedPack.id,
        drums_id: nextDrum?.loop_id || null,
        melody_id: nextMelody?.loop_id || null,
      });
    }
    return { nextDrum, nextMelody };
  }, [onChangeBeat]);

  const loadLoops = useCallback(async (selectedPack) => {
    const response = await fetch(`${API_URL}/api/sound-packs/${selectedPack.id}/loops`);
    if (!response.ok) throw new Error('Could not load this pack.');
    const data = await response.json();
    setGrouped(data);
    return data;
  }, []);

  const selectPack = useCallback(async (selectedPack) => {
    const context = getContext();
    reopeningRef.current = `${song.id}:${selectedPack.id}`;
    stopPlayback();
    setPack(selectedPack);
    setGrouped(null);
    setDecoded([]);
    setDrum(null);
    setMelody(null);
    setSingleLoop(false);
    setError('');
    setLoading(true);
    onChangeBpm(selectedPack.tempo || bpm);
    try {
      const loops = await loadLoops(selectedPack);
      if (!context) {
        const nextMelody = (loops.Melodies || []).find((loop) => loop.preview_url) || null;
        const nextDrum = nextMelody ? null : (loops.Drums || []).find((loop) => loop.preview_url);
        setMelody(nextMelody);
        setDrum(nextDrum);
        setSingleLoop(true);
        if (nextMelody || nextDrum) {
          onChangeBeat({
            pack_id: selectedPack.id,
            drums_id: nextDrum?.loop_id || null,
            melody_id: nextMelody?.loop_id || null,
          });
        }
        return;
      }
      const items = await decodeLoops(loops, {}, context);
      chooseDecoded(items, selectedPack);
      if (!items.length) {
        const fallback = (loops.Melodies || []).find((loop) => loop.preview_url)
          || (loops.Drums || []).find((loop) => loop.preview_url);
        setMelody(fallback || null);
        setDrum(null);
        setSingleLoop(Boolean(fallback));
        if (fallback) {
          onChangeBeat({ pack_id: selectedPack.id, drums_id: null, melody_id: fallback.loop_id });
        }
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [
    bpm,
    chooseDecoded,
    decodeLoops,
    getContext,
    loadLoops,
    onChangeBeat,
    onChangeBpm,
    song.id,
    stopPlayback,
  ]);

  useEffect(() => {
    const beat = song.beat;
    if (!beat?.pack_id || !packs.length || reopeningRef.current === `${song.id}:${beat.pack_id}`) {
      return;
    }
    const savedPack = packs.find((item) => String(item.id) === String(beat.pack_id));
    if (!savedPack) return;
    reopeningRef.current = `${song.id}:${beat.pack_id}`;
    setPack(savedPack);
    setLoading(true);
    setError('');
    loadLoops(savedPack)
      .then((loops) => {
        setDrum((loops.Drums || []).find((loop) => loop.loop_id === beat.drums_id) || null);
        setMelody((loops.Melodies || []).find((loop) => loop.loop_id === beat.melody_id) || null);
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [loadLoops, packs, song.beat, song.id]);

  useEffect(() => {
    if (reopeningRef.current?.startsWith(`${song.id}:`)) return;
    stopPlayback();
    setPack(null);
    setGrouped(null);
    setDecoded([]);
    setDrum(null);
    setMelody(null);
  }, [song.id, stopPlayback]);

  const startPlayback = async () => {
    const context = getContext();
    let nextDrum = drum;
    let nextMelody = melody;
    if (context && grouped && !decoded.length) {
      setLoading(true);
      const items = await decodeLoops(grouped, song.beat || {}, context);
      if (items.length) {
        const chosen = chooseDecoded(items, pack, song.beat || {});
        nextDrum = chosen.nextDrum;
        nextMelody = chosen.nextMelody;
      }
      setLoading(false);
    }
    stopPlayback();
    const playable = [nextDrum, nextMelody].filter((loop) => loop?.buffer);
    if (context && playable.length) {
      playbackRef.current = scheduleBuffers(context, gainRef.current, playable);
      setPlaying(true);
    } else {
      const fallback = nextMelody?.preview_url || nextDrum?.preview_url;
      if (!fallback) {
        setError('This pack does not have a playable loop.');
        return;
      }
      const audio = new Audio(fallback);
      audio.loop = true;
      audio.volume = volume;
      fallbackRef.current = audio;
      try {
        await audio.play();
        setPlaying(true);
      } catch (_error) {
        setError('Your browser could not play this loop.');
        return;
      }
    }
    if (!firstPlayRef.current) {
      firstPlayRef.current = true;
      onFirstPlay();
    }
  };

  const swapLoop = (group, loop) => {
    const nextDrum = group === 'Drums' ? loop : drum;
    const nextMelody = group === 'Melodies' ? loop : melody;
    setDrum(nextDrum);
    setMelody(nextMelody);
    onChangeBeat({
      pack_id: pack.id,
      drums_id: nextDrum?.loop_id || null,
      melody_id: nextMelody?.loop_id || null,
    });
    if (playing && contextRef.current) {
      playbackRef.current?.stop();
      const playable = [nextDrum, nextMelody].filter((item) => item?.buffer);
      playbackRef.current = scheduleBuffers(
        contextRef.current,
        gainRef.current,
        playable,
        contextRef.current.currentTime + 0.1,
      );
    }
  };

  const compatibleDrums = decoded.filter((loop) => (
    loop.group === 'Drums'
    && (!melody || (
      Number(loop.bpm) === Number(melody.bpm)
      && isCompatibleRatio(loop.duration, melody.duration)
    ))
  ));
  const compatibleMelodies = decoded.filter((loop) => (
    loop.group === 'Melodies'
    && (!drum || (
      Number(loop.bpm) === Number(drum.bpm)
      && isCompatibleRatio(loop.duration, drum.duration)
    ))
  ));

  return (
    <section
      className={`booth-beat-strip neon-card loop-card top-3 z-20 mb-8 overflow-hidden
        rounded-[1.75rem] border border-white/10 p-4 lg:sticky ${playing ? 'is-playing' : ''}`}
      style={{ '--glow': color, '--beat': `${60 / bpm}s` }}
      aria-label="Beat strip"
    >
      <div
        aria-hidden="true"
        className="loop-card-wash pointer-events-none absolute -right-20 -top-24 h-56 w-56
          rounded-full blur-3xl"
        style={{ backgroundColor: color }}
      />
      <div className="relative flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={playing ? stopPlayback : startPlayback}
          disabled={!pack || loading}
          className="neon-btn flex min-h-14 min-w-14 items-center justify-center rounded-full
            text-black disabled:opacity-40"
          style={{ backgroundColor: color }}
          aria-label={playing ? 'Stop beat' : 'Play beat'}
        >
          {playing ? <Pause className="h-6 w-6" /> : <Play className="ml-1 h-6 w-6" />}
        </button>

        <div className="min-w-[12rem] flex-1">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-white/45">
            {loading ? 'Loading beat' : 'Write over a beat'}
          </p>
          <p className="font-heading text-xl font-black">{pack?.name || 'Pick a sound pack'}</p>
          {pack && (
            <p className="text-sm font-bold text-white/55">
              {pack.tempo || bpm} BPM{pack.key ? ` · ${pack.key}` : ''}
              {singleLoop ? ' · Single loop for this pack' : ''}
            </p>
          )}
        </div>

        <div className="booth-equalizer flex h-10 items-end gap-1" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((bar) => (
            <span key={bar} className="eq-bar w-1 rounded-full" style={{
              animationDelay: `calc(var(--beat) * ${bar / 8})`,
              animationDuration: 'var(--beat)',
              backgroundColor: color,
              height: `${35 + ((bar * 23) % 60)}%`,
              opacity: playing ? 1 : 0.28,
            }} />
          ))}
        </div>

        <label className="flex min-h-11 items-center gap-2 text-sm font-bold text-white/60">
          Volume
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={(event) => setVolume(Number(event.target.value))}
            className="w-24 accent-white"
          />
        </label>
      </div>

      <div className="relative mt-4 flex gap-2 overflow-x-auto pb-1" aria-label="Sound packs">
        <button
          type="button"
          onClick={() => {
            const choice = pickRandomPack(packs, bpm);
            if (choice) selectPack(choice);
          }}
          disabled={!packs.length}
          className="flex min-h-12 shrink-0 items-center gap-2 rounded-full border
            border-[color:var(--glow)] px-4 text-base font-black disabled:opacity-40"
        >
          <Shuffle className="h-4 w-4" /> Find a beat for this
        </button>
        {sortedPacks.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => selectPack(item)}
            aria-label={`${item.name}, ${item.tempo || 'no'} BPM`}
            aria-pressed={pack?.id === item.id}
            className={`flex min-h-12 shrink-0 items-center gap-2 rounded-full border px-3
              text-left ${pack?.id === item.id
                ? 'border-[color:var(--glow)] bg-white/10' : 'border-white/10'}`}
          >
            {item.cover ? (
              <img src={item.cover} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10">
                <Disc3 className="h-4 w-4" />
              </span>
            )}
            <span>
              <span className="block text-sm font-black">{item.name}</span>
              <span className="block text-xs text-white/45">{item.tempo || 'No tempo'} BPM</span>
            </span>
          </button>
        ))}
      </div>

      {(compatibleDrums.length > 1 || compatibleMelodies.length > 1) && (
        <div className="relative mt-3 flex flex-wrap gap-2">
          {compatibleDrums.map((loop) => (
            <button
              key={loop.loop_id}
              type="button"
              onClick={() => swapLoop('Drums', loop)}
              className="min-h-11 rounded-full border px-3 text-sm font-black"
              style={{
                borderColor: GROUP_HEX.Drums,
                color: drum?.loop_id === loop.loop_id ? '#0B0B0C' : GROUP_HEX.Drums,
                backgroundColor: drum?.loop_id === loop.loop_id ? GROUP_HEX.Drums : 'transparent',
              }}
            >
              Drums · {loop.title}
            </button>
          ))}
          {compatibleMelodies.map((loop) => (
            <button
              key={loop.loop_id}
              type="button"
              onClick={() => swapLoop('Melodies', loop)}
              className="min-h-11 rounded-full border px-3 text-sm font-black"
              style={{
                borderColor: GROUP_HEX.Melodies,
                color: melody?.loop_id === loop.loop_id ? '#0B0B0C' : GROUP_HEX.Melodies,
                backgroundColor: melody?.loop_id === loop.loop_id
                  ? GROUP_HEX.Melodies : 'transparent',
              }}
            >
              Melody · {loop.title}
            </button>
          ))}
        </div>
      )}
      {error && <p role="alert" className="relative mt-3 text-base font-bold text-[#FFB19C]">{error}</p>}
    </section>
  );
};

export default BeatStrip;
